import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as _ from 'lodash';
import throwSubmissionError from 'cl-form/utils/throwSubmissionError';
import Button, {ButtonProps} from '@material-ui/core/Button';
import IconButton, {IconButtonProps} from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Tooltip from '@material-ui/core/Tooltip';
import {makeStyles, createStyles} from '@material-ui/core/styles';
import {Theme} from '@material-ui/core/styles/createMuiTheme';
import InlineButton, {InlineButtonProps} from '../components/InlineButton';
import connectWithRouter from '../components/redux/connectWithRouter';
import {
  ACTION_DISPLAY_TYPES,
  OBJECT_TYPES_NAMES,
  ActionsManagerProps,
  ActionsDisplayProps,
  ActionProps,
} from './ActionsManagerConsts';
import {EBAY_LISTINGS_ACTIONS_PROPS} from './EbayListingsActions/EbayListingsActionsConsts';
import {AMAZON_LISTINGS_ACTIONS_PROPS} from './AmazonListingsActions/AmazonListingsActionsConsts';
import {GENERAL_ACTIONS_PROPS} from './GeneralActions/GeneralActionsConsts';
import {createStructuredSelector} from 'reselect';
import {createDeepEqualSelector} from './ActionsManagerSelectors';
import SelectionDialogItem, {SelectionDialogItemProps} from 'cl-dialogs/SelectionDialog/SelectionDialogItem';
import {camelToDashed} from 'cl-automation-utils';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
const OBJECT_TYPE_PROPS = {
  [OBJECT_TYPES_NAMES.LISTING]: EBAY_LISTINGS_ACTIONS_PROPS,
  [OBJECT_TYPES_NAMES.GENERAL]: GENERAL_ACTIONS_PROPS,
  [OBJECT_TYPES_NAMES.AMAZON_LISTING]: AMAZON_LISTINGS_ACTIONS_PROPS,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionsElement: {
      '& + &': {
        marginLeft: theme.spacing(1),
      },
    },
  })
);
const ActionsButton = React.forwardRef<any, ButtonProps & ActionsDisplayProps>((props, ref) => {
  const classes = useStyles();
  const {label, icon, IconAfterLabel, tooltip, ...otherProps} = props;
  const buttonContent = [];
  if (icon && !IconAfterLabel) {
    buttonContent.push(icon);
  }
  buttonContent.push(label);
  if (icon && IconAfterLabel) {
    buttonContent.push(icon);
  }
  if (tooltip) {
    return (
      <Tooltip className={classes.actionsElement} title={tooltip}>
        <span>
          <Button {...otherProps}>{buttonContent}</Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Button ref={ref} className={classes.actionsElement} {...otherProps}>
      {buttonContent}
    </Button>
  );
});

const ActionsIconButton = React.forwardRef<any>((props: IconButtonProps & ActionsDisplayProps, ref) => {
  const classes = useStyles();
  const {icon, tooltip, ...otherProps} = props;
  if (tooltip) {
    return (
      <Tooltip className={classes.actionsElement} title={tooltip}>
        <span>
          <IconButton ref={ref} {...otherProps}>
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <IconButton ref={ref} className={classes.actionsElement} {...otherProps}>
      {icon}
    </IconButton>
  );
});

const ActionsSplitButton = React.forwardRef<any, ButtonProps & ActionsDisplayProps>(
  ({children, color, variant, ...props}, ref) => {
    return (
      <ButtonGroup color={color} variant={variant}>
        <ActionsButton ref={ref} {...props} />
        <Menu
          propagatePropsTo="button"
          color={color}
          size="small"
          button={
            <Button>
              <ArrowDropDownIcon />
            </Button>
          }
        >
          {children}
        </Menu>
      </ButtonGroup>
    );
  }
);

const ActionsMenuItem = React.forwardRef<any>((props: SelectionDialogItemProps & ActionsDisplayProps, ref) => {
  return <SelectionDialogItem ref={ref as any} component={MenuItem} {...props} />;
});

const ActionsInlineButton = React.forwardRef<any>((props: InlineButtonProps & ActionsDisplayProps, ref) => {
  const {label, ...otherProps} = props;
  return (
    <InlineButton buttonRef={ref} {...otherProps}>
      {label}
    </InlineButton>
  );
});

function renderDisplayElement(displayType, props, tooltip) {
  switch (displayType) {
    case ACTION_DISPLAY_TYPES.RAISED_BUTTON:
      return <ActionsButton variant="contained" tooltip={tooltip} {...props} />;
    case ACTION_DISPLAY_TYPES.FLAT_BUTTON:
      return <ActionsButton variant="text" tooltip={tooltip} {...props} />;
    case ACTION_DISPLAY_TYPES.ICON_BUTTON:
      return <ActionsIconButton tooltip={tooltip} {...props} />;
    case ACTION_DISPLAY_TYPES.MENU_ITEM:
      return <ActionsMenuItem {...props} />;
    case ACTION_DISPLAY_TYPES.SPLIT_BUTTON:
      return <ActionsSplitButton {...props} />;
    case ACTION_DISPLAY_TYPES.INLINE_BUTTON:
      return <ActionsInlineButton {...props} />;
    default:
      console.error(
        'Unknown action display type',
        displayType,
        '. Maybe you need to add a new type at ActionsManager::renderDisplayElement?'
      );
      return;
  }
}

class Actions extends React.PureComponent<ActionsManagerProps> {
  static contextTypes = {
    utils: PropTypes.object.isRequired,
  };

  render() {
    const {
      types,
      displayType,
      objects,
      objectType,
      handleSubmit,
      lastActionColor,
      noObjects,
      additionalActionData,
      id,
    } = this.props;
    let elements = _.filter(
      _.map(types, (actionType, index) => {
        const computedDisplayType =
          displayType === ACTION_DISPLAY_TYPES.SPLIT_BUTTON && index !== 0
            ? ACTION_DISPLAY_TYPES.MENU_ITEM
            : displayType;
        const actionProps: ActionProps = OBJECT_TYPE_PROPS[objectType][actionType];
        let affectedObjects = [];
        if (!noObjects) {
          if (actionProps.singleObjectOnly && _.size(objects) !== 1) {
            return null;
          }
          if (!(actionProps.objectIsOptional && _.isEmpty(objects))) {
            affectedObjects = _.filter(objects, object => {
              return (
                object &&
                (!actionProps.shouldRender || actionProps.shouldRender(object, this.props.storeProps[actionType]))
              );
            });
            if (_.isEmpty(affectedObjects)) {
              return null;
            }
          }
        } else {
          if (actionProps.shouldRender) {
            if (!actionProps.shouldRender(null, this.props.storeProps[actionType], additionalActionData)) {
              return null;
            }
          }
        }
        let onClick;
        let ref;
        if (actionProps.action) {
          ref = React.createRef();
          const action = handleSubmit
            ? handleSubmit(async () => {
                try {
                  return await actionProps.action(affectedObjects, ref, additionalActionData)();
                } catch (e) {
                  return throwSubmissionError(e);
                }
              })
            : actionProps.action(affectedObjects, ref, additionalActionData);
          // noinspection JSDeprecatedSymbols
          onClick =
            action && actionProps.verifyUser ? () => this.context.utils.verifyUserStatusAndCall(action) : action;
        }
        const disabled = actionProps.isDisabled
          ? actionProps.isDisabled(affectedObjects, this.props.storeProps[actionType])
          : undefined;
        const displayElementsProps = {
          key: actionType,
          ...actionProps.displayElementsProps[computedDisplayType],
          onClick,
          disabled,
          id: id ? `${id}-${camelToDashed(actionType)}` : undefined,
          className: `cla-${camelToDashed(actionType)}`,
        };
        const tooltip = actionProps.getTooltip
          ? actionProps.getTooltip(affectedObjects, this.props.storeProps[actionType])
          : undefined;
        return renderDisplayElement(computedDisplayType, {...displayElementsProps, ref}, tooltip);
      })
    );
    if (lastActionColor) {
      elements = _.map(elements, (element, index) =>
        index === elements.length - 1 ? React.cloneElement(element, {color: lastActionColor}) : element
      );
    }
    if (displayType === ACTION_DISPLAY_TYPES.SPLIT_BUTTON && !_.isEmpty(elements)) {
      return React.cloneElement(elements[0], {children: elements.slice(1)});
    }
    return elements;
  }
}

export const getActionsStoreProps = (state, props: ActionsManagerProps) => {
  const storeProps = {};
  const objectTypeProps = OBJECT_TYPE_PROPS[props.objectType];
  _.forEach(props.types, (actionType: string) => {
    if (_.isFunction(objectTypeProps[actionType].getStoreProps)) {
      storeProps[actionType] = objectTypeProps[actionType].getStoreProps(state);
    }
  });
  return storeProps;
};

export const actionsManagerMapStateToProps = createStructuredSelector(
  {
    storeProps: getActionsStoreProps,
  },
  createDeepEqualSelector
);

export default connectWithRouter(actionsManagerMapStateToProps, {})<ActionsManagerProps>(Actions);
